import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import {
  flattenAvatarsAndVariants,
  getAvatarVariantAbilityContext,
  variantHasTtsModelVersion,
} from '@avatar/utils/avatar';
import type { Actor } from '@avatar/types';
import logger from 'util/logger';
import Cache from 'components/ScriptStudio/utils/cache';
import {
  ActorAccessLevel,
  ActorVariant,
} from '../../../../server/graphql/__generated.types';

const AvatarContext = createContext<{
  queryAvatar: (args: {
    avatarId?: number;
    variantId?: number;
    modelVersion?: string;
    withAbilities?: boolean;
    debug?: boolean;
    disabled?: boolean;
  }) => AvatarContextQueryAvatarResult;
}>({
  queryAvatar: () => ({
    avatar: undefined,
    variant: undefined,
    abilities: null,
    ttsVersion: undefined,
    state: 'READ_ONLY',
  }),
});

export const useAvatarContext = () => {
  return useContext(AvatarContext);
};

export type AvatarContextQueryAvatarResult = {
  avatar: Actor | undefined;
  variant: ActorVariant | undefined;
  abilities: ReturnType<typeof getAvatarVariantAbilityContext> | null;
  ttsVersion: string | undefined;
  state: 'READ_ONLY' | 'AVAILABLE' | 'DISABLED';
};

export default function AvatarProvider({
  children,
  projectAvatars,
}: {
  projectAvatars: Actor[];
  children: React.ReactNode;
}) {
  const cacheRef = useRef(new Cache({ debug: false }));
  // avoid any fancy computations unless in universal mode
  const flattenedAvatars = useMemo(
    () => flattenAvatarsAndVariants(projectAvatars),
    [projectAvatars]
  );
  const queryAvatar = useCallback(
    ({
      avatarId,
      variantId,
      modelVersion,
      withAbilities,
      debug,
      disabled,
    }: {
      avatarId?: number;
      variantId?: number;
      modelVersion?: string;
      withAbilities?: boolean;
      debug?: boolean;
      disabled?: boolean;
    }): AvatarContextQueryAvatarResult => {
      if (disabled) {
        return {
          avatar: undefined,
          variant: undefined,
          abilities: null,
          ttsVersion: undefined,
          state: 'DISABLED',
        };
      }
      if (!avatarId && !variantId) {
        return {
          avatar: undefined,
          variant: undefined,
          abilities: null,
          ttsVersion: undefined,
          // should never occur - but default to read only if it does...
          state: 'READ_ONLY',
        };
      }
      const avatar = avatarId
        ? flattenedAvatars.avatarById[avatarId]
        : flattenedAvatars.avatarByVariantId[variantId as number];
      if (!avatar) {
        return {
          avatar: undefined,
          variant: undefined,
          abilities: null,
          ttsVersion: undefined,
          state: 'READ_ONLY',
        };
      }
      const variant = flattenedAvatars.variant[variantId as number];
      if (!variant) {
        return {
          avatar,
          variant: undefined,
          abilities: null,
          ttsVersion: undefined,
          state: 'READ_ONLY',
        };
      }
      if (!modelVersion) {
        return {
          avatar,
          variant,
          abilities: null,
          ttsVersion: modelVersion || undefined,
          state: 'READ_ONLY',
        };
      }
      // if the model version is not found on the variant, return READ_ONLY
      if (!variantHasTtsModelVersion(variant, modelVersion)) {
        return {
          avatar,
          variant,
          abilities: null,
          ttsVersion: modelVersion,
          state: 'READ_ONLY',
        };
      }
      // handle former access level logic
      if (avatar.accessLevel === ActorAccessLevel.Locked) {
        return {
          avatar,
          variant,
          abilities: null,
          ttsVersion: modelVersion || undefined,
          state: 'READ_ONLY',
        };
      }
      if (withAbilities) {
        const cacheKey = `ability:${avatarId}:${variantId}:${modelVersion}`;
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
          return cached;
        }
        const abilities = getAvatarVariantAbilityContext(variant, modelVersion);
        const result: AvatarContextQueryAvatarResult = {
          avatar,
          variant,
          abilities,
          ttsVersion: modelVersion || undefined,
          state: 'AVAILABLE',
        };
        cacheRef.current.set(cacheKey, result);
        if (debug) {
          logger.info('queryAvatar', result);
        }
        return result;
      }
      return {
        avatar,
        variant,
        abilities: null,
        ttsVersion: modelVersion || undefined,
        state: 'AVAILABLE',
      };
    },
    [flattenedAvatars]
  );
  const value = useMemo(() => ({ queryAvatar }), [queryAvatar]);
  return (
    <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>
  );
}
